import { useState, type FormEvent } from 'react';
import type { Vendor } from '@mvcp/contracts';
import { proposeVendor } from '../api';
import { COUNTRY_NAME_OPTIONS } from '../lib/country';
import { DEVICE_CATEGORY_OPTIONS } from '../lib/device-category';
import { MAX_TEXT_LEN } from '../lib/field-limits';

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Propose a new Vendor when it is absent from the Directory (Story 2.5, FR
 * propose-new). Mirrors `SitesView`'s create-form: controlled inputs, an
 * in-flight `submitting` guard (prevents accidental double-submit — there is no
 * server-side dedup), an inline `role="alert"` error (never silently succeed),
 * and a submit-label swap. On success calls `onCreated(vendor)` so the parent
 * reloads the Directory and collapses the form. `onCancel` collapses without
 * submitting. The proposed Vendor enters an `unverified`, tenant-proposed state
 * server-side — this form sends AC1's fields: `name` (required) plus optional
 * `country`/`category`/`primaryProduct`/`complianceContact`.
 */
export function ProposeVendorForm({
  onCreated,
  onCancel,
  suggestions,
}: {
  onCreated: (vendor: Vendor) => void;
  onCancel: () => void;
  /**
   * Existing directory values offered as `<datalist>` autocomplete for the one remaining
   * free-text field (Story 2.5 UX). Purely advisory — a manager may still type a new
   * primary product, which is the point of proposing.
   *
   * `countries` and `categories` were dropped in DEV-4426 round 2 (QA TC-7). Both fields are
   * now closed `<select>`s, and a suggestion list BUILT FROM STORED VALUES was the mechanism
   * of the defect rather than an innocent bystander: every value a manager typed became an
   * offered option for the next one, so one typo propagated as an autocomplete entry. The
   * prop is narrowed rather than left accepted-and-ignored, so a caller still passing them
   * fails to compile instead of silently having them dropped.
   */
  suggestions?: {
    primaryProducts?: string[];
  };
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [primaryProduct, setPrimaryProduct] = useState('');
  const [complianceContact, setComplianceContact] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Vendor name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const vendor = await proposeVendor({
        name: trimmedName,
        ...(country.trim() ? { country: country.trim() } : {}),
        ...(category.trim() ? { category: category.trim() } : {}),
        ...(primaryProduct.trim() ? { primaryProduct: primaryProduct.trim() } : {}),
        ...(complianceContact.trim() ? { complianceContact: complianceContact.trim() } : {}),
      });
      onCreated(vendor);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't propose. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className="stack"
      aria-label="Propose a new vendor"
      style={{ gap: 10 }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
        <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
          <span className="field-label">Name</span>
          <input
            aria-label="Vendor name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={MAX_TEXT_LEN}
            style={fieldStyle}
          />
        </label>
        <label className="stack" style={{ gap: 4, flex: '0 1 170px' }}>
          <span className="field-label">Country</span>
          <select
            aria-label="Vendor country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={fieldStyle}
          >
            <option value="">—</option>
            {COUNTRY_NAME_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="stack" style={{ gap: 4, flex: '0 1 170px' }}>
          <span className="field-label">Category</span>
          <select
            aria-label="Vendor category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={fieldStyle}
          >
            <option value="">—</option>
            {DEVICE_CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
          <span className="field-label">Primary product</span>
          <input
            aria-label="Vendor primary product"
            list="propose-vendor-products"
            value={primaryProduct}
            onChange={(e) => setPrimaryProduct(e.target.value)}
            maxLength={MAX_TEXT_LEN}
            style={fieldStyle}
          />
        </label>
        <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
          <span className="field-label">Compliance contact</span>
          <input
            aria-label="Vendor compliance contact"
            value={complianceContact}
            onChange={(e) => setComplianceContact(e.target.value)}
            maxLength={MAX_TEXT_LEN}
            style={fieldStyle}
          />
        </label>
        <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
          {submitting ? 'Proposing…' : 'Propose vendor'}
        </button>
        <button type="button" className="icon-button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>
      <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
        {error && (
          <span className="status-pill" data-tone="bad">
            <span className="status-pill__dot" aria-hidden="true" />
            {error}
          </span>
        )}
      </div>
      {/* Country and Category no longer have one: they are closed <select>s as of DEV-4426
          round 2, and a suggestion list derived from stored values is exactly what made them
          self-widening. Primary product is still genuinely free text and keeps its. */}
      <datalist id="propose-vendor-products">
        {(suggestions?.primaryProducts ?? []).map((v) => (
          <option key={v} value={v} />
        ))}
      </datalist>
    </form>
  );
}
